/**
 * Mermaid repairs for the non-flowchart dialects (ER, sequence, C4, journey),
 * shared by the API sanitiser (server, at persistence) and the web renderer
 * (browser, late rescue). Flowcharts live in mermaidFlowchartRepair.ts.
 *
 * Every rule repairs a shape that was reproduced as a parse error against the
 * real Mermaid 11.12.3 parser (see __tests__/mermaidRepair.test.ts, which runs
 * the parser itself — string assertions alone cannot show a repair parses):
 *
 *  ER        multi-word / punctuated relationship label → quoted; missing
 *            label → empty quoted label; trailing `;` dropped; attribute type
 *            `array<string>` / `decimal(10,2)` → `array_string` / `decimal`.
 *  sequence  `;` inside message text → `,`; unclosed `alt/opt/loop/par/...`
 *            gets its `end`; a stray `end` is dropped; `A->>B` (no colon) →
 *            `A->>B:`; `Note A: x` → `Note over A: x`.
 *  C4        `Rel(a, b)` (no label) → empty label; unquoted element arguments
 *            → quoted; quotes nested inside a string → single quotes; missing
 *            `)` / `}` appended.
 *  journey   `section A: B` → `section A - B`; `;` in a task → `,`.
 *
 * Contract (pinned by tests):
 *  - A diagram that already parses is returned byte-for-byte: each rule matches
 *    only a shape the parser rejects.
 *  - Idempotent: repair(repair(x)) === repair(x).
 *  - Never throws; anything not understood is left unchanged. Oversized input
 *    is returned as is.
 *
 * Deliberately NOT repaired (would invent data or guess intent): a journey task
 * with no score, unknown C4 macros, C4 written as `a --> b`, undeclared C4
 * aliases. Those fall through to the Regenerate action (#1117).
 */

export type RepairDialect = 'er' | 'sequence' | 'c4' | 'journey';

const MAX_CODE_LENGTH = 200_000;
const MAX_LINE_LENGTH = 4_000;

/** First diagram keyword after YAML front matter and `%%` directives. */
function headerLine(code: string): string {
  const lines = code
    .replace(/^\s*---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, '')
    .split('\n');
  for (const l of lines) {
    const t = l.trim();
    if (t !== '' && !t.startsWith('%%')) return t.toLowerCase();
  }
  return '';
}

export function detectRepairDialect(code: string): RepairDialect | null {
  const h = headerLine(code);
  if (/^erdiagram\b/.test(h)) return 'er';
  if (/^sequencediagram\b/.test(h)) return 'sequence';
  if (/^c4(context|container|component|dynamic|deployment)\b/.test(h)) return 'c4';
  if (/^journey\b/.test(h)) return 'journey';
  return null;
}

function mapLines(code: string, fn: (lines: string[]) => string[]): string {
  if (code.length > MAX_CODE_LENGTH) return code;
  const eol = code.includes('\r\n') ? '\r\n' : '\n';
  const lines = code.split(/\r?\n/);
  if (lines.some(l => l.length > MAX_LINE_LENGTH)) return code;
  try {
    const res = fn(lines);
    // Untouched input is returned as is (keeps mixed line endings intact).
    if (res.length === lines.length && res.every((l, i) => l === lines[i])) return code;
    return res.join(eol);
  } catch {
    return code;
  }
}

/* ------------------------------------------------------------------ ER */

const ER_REL =
  /^(\s*)("[^"]+"|[A-Za-z_][\w-]*)(\s+)([|}o]{1,2}(?:--|\.\.)[|{o]{1,2})(\s+)("[^"]+"|[A-Za-z_][\w-]*)(.*)$/;
const ER_SIMPLE_LABEL = /^[A-Za-z_][\w-]*$/;
const ER_ATTR_TYPE = /^(\s*)([A-Za-z_][\w-]*)(\([^)\n]*\))?(<[^>\n]*>)?(\s+\S.*)$/;

function repairErLine(line: string, inBlock: boolean): string {
  if (inBlock) {
    const m = ER_ATTR_TYPE.exec(line);
    if (!m) return line;
    const [, indent, base, paren, generic, rest] = m;
    let type = base;
    let changed = false;
    if (generic) {
      type = `${base}_${generic.slice(1, -1).replace(/[^\w]+/g, '_').replace(/^_+|_+$/g, '')}`;
      changed = true;
    }
    // decimal(10,2) is a parse error (dropped); varchar(255) is fine and the
    // line is returned untouched below.
    if (paren && paren.includes(',')) changed = true;
    else if (paren && !changed) return line;
    else if (paren) type += paren;
    return changed ? `${indent}${type}${rest}` : line;
  }
  const m = ER_REL.exec(line);
  if (!m) return line;
  const [, indent, left, s1, card, s2, right, tailRaw] = m;
  let tail = tailRaw.trim();
  const hadSemicolon = tail.endsWith(';');
  if (hadSemicolon) tail = tail.slice(0, -1).trimEnd();
  const head = `${indent}${left}${s1}${card}${s2}${right}`;
  if (tail === '') {
    return `${head} : ""`;
  }
  const lm = /^:\s*(.*)$/.exec(tail);
  if (!lm) return line; // not understood
  const label = lm[1].trim();
  if (label === '') return `${head} : ""`;
  const quoted = label.startsWith('"') && label.endsWith('"') && label.length >= 2 && !label.slice(1, -1).includes('"');
  // Already valid: return the line byte-for-byte (spacing included).
  if (!hadSemicolon && (quoted || ER_SIMPLE_LABEL.test(label))) return line;
  if (quoted) return `${head} : ${label}`;
  if (ER_SIMPLE_LABEL.test(label)) return `${head} : ${label}`;
  return `${head} : "${label.replace(/"/g, "'")}"`;
}

export function repairEr(code: string): string {
  return mapLines(code, lines => {
    let inBlock = false;
    return lines.map(line => {
      const t = line.trim();
      if (inBlock) {
        if (t.startsWith('}')) { inBlock = false; return line; }
        if (t === '' || t.startsWith('%%')) return line;
        return repairErLine(line, true);
      }
      if (t.startsWith('%%')) return line;
      if (/\{\s*$/.test(t) && !ER_REL.test(line)) { inBlock = true; return line; }
      return repairErLine(line, false);
    });
  });
}

/* ------------------------------------------------------------ sequence */

const SEQ_ARROW = '(?:-->>|->>|-->|->|--x|-x|--\\)|-\\))[+-]?';
const SEQ_MSG_LINE = new RegExp(`^\\s*[^\\s:;]+\\s*${SEQ_ARROW}\\s*[^\\s:;]+\\s*:`);
const SEQ_NOCOLON = new RegExp(`^(\\s*[^\\s:;]+\\s*${SEQ_ARROW}\\s*[^\\s:;]+)\\s*$`);
const SEQ_STATEMENT_START = new RegExp(
  `^\\s*(?:[^\\s:;]+\\s*${SEQ_ARROW}|(?:participant|actor|create|destroy|note|loop|alt|else|opt|par_over|par|and|critical|option|break|rect|box|end|activate|deactivate|autonumber|title|link|links|properties|details)\\b)`,
  'i',
);
const SEQ_OPEN = /^\s*(?:alt|opt|loop|par_over|par|critical|break|rect|box)(?=\s|$)/i;
/** Rules below skip lines longer than this: they are bounded-cost regexes, not a parser. */
const MAX_SEQ_RULE_LINE = 1000;
const SEQ_END = /^\s*end\s*$/i;
const SEQ_NOTE_NOPLACE = /^(\s*)note(\s+)(?!over\b|left\s+of\b|right\s+of\b)([^\s:;,]+(?:\s*,\s*[^\s:;,]+)?\s*:)/i;

function joinSemicolons(line: string): string {
  if (!line.includes(';')) return line;
  const isMsg = SEQ_MSG_LINE.test(line) || /^\s*note\b[^;]*:/i.test(line);
  if (!isMsg) return line;
  // `;` that ends an entity code (`#59;`, `#9829;`) is text, not a separator.
  const parts = line.split(/(?<!#\w{1,10});/);
  // `A->>B: x; B->>A: y` is two valid statements: only join when a later
  // segment is not itself a statement start.
  const bad = parts.slice(1).some(p => p.trim() !== '' && !SEQ_STATEMENT_START.test(p));
  if (!bad) return line;
  return parts
    .map(p => p.trim())
    .filter((p, i) => i === 0 || p !== '')
    .reduce((acc, p, i) => (i === 0 ? p : `${acc}, ${p}`), '')
    .replace(/^/, line.match(/^\s*/)?.[0] ?? '');
}

export function repairSequence(code: string): string {
  return mapLines(code, lines => {
    let depth = 0;
    const out: string[] = [];
    for (let line of lines) {
      if (line.trim().startsWith('%%')) { out.push(line); continue; }
      if (line.length <= MAX_SEQ_RULE_LINE) {
        line = joinSemicolons(line);
        const noc = SEQ_NOCOLON.exec(line);
        if (noc) line = `${noc[1]}:`;
        line = line.replace(SEQ_NOTE_NOPLACE, '$1Note$2over $3');
      }
      if (SEQ_OPEN.test(line)) depth++;
      else if (SEQ_END.test(line)) {
        if (depth === 0) continue; // stray end
        depth--;
      }
      out.push(line);
    }
    while (depth-- > 0) out.push('end');
    return out;
  });
}

/* ------------------------------------------------------------------ C4 */

const C4_REL = /^(\s*)((?:Bi)?Rel(?:Index)?(?:_(?:U|D|L|R|Up|Down|Left|Right|Back))?)\((.*)\)\s*$/;
const C4_ELEMENT =
  /^(\s*)((?:Person|System|Container|Component|Node|Deployment_Node|Boundary|Enterprise_Boundary|System_Boundary|Container_Boundary)(?:Db|Queue)?(?:_Ext)?)\((.*)\)(\s*\{?\s*)$/;

/** Split on top-level commas, honouring double quotes. */
function splitArgs(args: string): string[] {
  const out: string[] = [];
  let cur = '';
  let q = false;
  for (const ch of args) {
    if (ch === '"') q = !q;
    if (ch === ',' && !q) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/**
 * Re-quote a string argument whose interior holds double quotes: a quote is a
 * delimiter only when it is at an argument edge (start, end, next to a comma).
 */
function fixNestedQuotes(args: string): string {
  // Named parameters ($tags="v1") legitimately carry quotes mid-argument.
  if (/\$\w+\s*=/.test(args)) return args;
  const chars = args.split('');
  const isEdge = (i: number): boolean => {
    let b = i - 1;
    while (b >= 0 && chars[b] === ' ') b--;
    let a = i + 1;
    while (a < chars.length && chars[a] === ' ') a++;
    return b < 0 || chars[b] === ',' || a >= chars.length || chars[a] === ',';
  };
  for (let i = 0; i < chars.length; i++) {
    if (chars[i] === '"' && !isEdge(i)) chars[i] = "'";
  }
  return chars.join('');
}

function quoteArg(a: string): string {
  const t = a.trim();
  if (t === '' || t.startsWith('"') || t.startsWith('$')) return a;
  return ` "${t.replace(/"/g, "'")}"`;
}

function countOutsideQuotes(s: string, ch: string): number {
  let q = false;
  let n = 0;
  for (const c of s) {
    if (c === '"') q = !q;
    else if (!q && c === ch) n++;
  }
  return n;
}

export function repairC4(code: string): string {
  return mapLines(code, lines => {
    let braces = 0;
    const out = lines.map(line => {
      const t = line.trim();
      if (t === '' || t.startsWith('%%')) return line;

      // Missing close paren on a macro line.
      let cur = line;
      const open = /^(\s*)([A-Za-z_]+)\(/.exec(cur);
      // An odd number of quotes means a string continues on the next line: leave it.
      const evenQuotes = (cur.split('"').length - 1) % 2 === 0;
      if (open && evenQuotes && countOutsideQuotes(cur, '(') > countOutsideQuotes(cur, ')')) {
        const m = /^(.*?)(\s*\{)?\s*$/.exec(cur);
        cur = `${m?.[1] ?? cur})${m?.[2] ?? ''}`;
      }

      const rel = C4_REL.exec(cur);
      if (rel) {
        const [, indent, name, argsRaw] = rel;
        let args = fixNestedQuotes(argsRaw);
        let parts = splitArgs(args);
        const labelIdx = name.startsWith('RelIndex') ? 3 : 2; // RelIndex(index, from, to, label)
        if (parts.length === labelIdx) parts = [...parts, ' ""']; // no label
        args = parts.map((p, i) => (i === labelIdx ? quoteArg(p) : p)).join(',');
        return `${indent}${name}(${args})`;
      }
      const el = C4_ELEMENT.exec(cur);
      if (el) {
        const [, indent, name, argsRaw, tailRaw] = el;
        const args = fixNestedQuotes(argsRaw);
        const parts = splitArgs(args).map((p, i) => (i === 0 ? p : quoteArg(p)));
        if (/\{\s*$/.test(tailRaw)) braces++;
        return `${indent}${name}(${parts.join(',')})${tailRaw}`;
      }
      if (/^\s*\}\s*$/.test(cur)) braces--;
      return cur;
    });
    while (braces-- > 0) out.push('}');
    return out;
  });
}

/* ------------------------------------------------------------- journey */

export function repairJourney(code: string): string {
  return mapLines(code, lines => {
    let inFront = lines.length > 0 && lines.findIndex(l => l.trim() !== '') >= 0 && lines[lines.findIndex(l => l.trim() !== '')].trim() === '---';
    let frontSeen = false;
    let inAccBlock = false;
    return lines.map(line => {
      const t = line.trim();
      if (inFront) {
        if (t === '---') { if (frontSeen) inFront = false; else frontSeen = true; }
        return line;
      }
      if (inAccBlock) { if (t.includes('}')) inAccBlock = false; return line; }
      if (t === '' || t.startsWith('%%')) return line;
      if (/^accDescr\s*\{/i.test(t)) { if (!t.includes('}')) inAccBlock = true; return line; }
      if (/^(journey|title|acctitle|accdescr)\b/i.test(t)) return line;
      const sec = /^(\s*section\s+)(.*)$/i.exec(line);
      if (sec) {
        return sec[2].includes(':') || /;(?<!#\w{1,10};)/.test(sec[2])
          ? `${sec[1]}${sec[2].replace(/\s*:\s*|\s*(?<!#\w{1,10});\s*/g, ' - ')}`
          : line;
      }
      return line.replace(/(?<!#\w{1,10});/g, ',');
    });
  });
}

/* ------------------------------------------------------------ dispatch */

/** Repair a diagram of any non-flowchart dialect covered here; others pass through. */
export function repairMermaid(code: string): string {
  switch (detectRepairDialect(code)) {
    case 'er': return repairEr(code);
    case 'sequence': return repairSequence(code);
    case 'c4': return repairC4(code);
    case 'journey': return repairJourney(code);
    default: return code;
  }
}
